import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import { Checkbox } from '../../../components/ui/Checkbox';

const CateringPackages = ({ onCustomizeClick }) => {
  const [selectedPackage, setSelectedPackage] = useState(null);
  const [customizations, setCustomizations] = useState([]);

  const packages = [
    {
      id: 'breakfast',
      name: 'Morning Delight',
      icon: 'Coffee',
      price: 18,
      description: 'Perfect for breakfast meetings and morning events',
      includes: [
        'Assorted fresh cornetti and pastries',
        'Italian coffee and cappuccino service',
        'Fresh fruit platter',
        'Orange juice and water',
        'Disposable serving ware'
      ],
      addons: [
        { id: 'yogurt', name: 'Greek yogurt parfaits', price: 4 },
        { id: 'sandwiches', name: 'Breakfast sandwiches', price: 6 },
        { id: 'premium-coffee', name: 'Premium coffee bar', price: 5 }
      ],
      minGuests: 10,
      popular: false
    },
    {
      id: 'lunch',
      name: 'Italian Lunch',
      icon: 'UtensilsCrossed',
      price: 25,
      description: 'Authentic Italian lunch experience for your team',
      includes: [
        'Selection of Italian sandwiches',
        'Fresh garden salad with house dressing',
        'Assorted Italian cookies',
        'Beverages (soft drinks, water)',
        'Complete serving setup'
      ],
      addons: [
        { id: 'pasta', name: 'Hot pasta station', price: 8 },
        { id: 'soup', name: 'Italian soup service', price: 5 },
        { id: 'gelato', name: 'Gelato dessert bar', price: 6 }
      ],
      minGuests: 15,
      popular: true
    },
    {
      id: 'dinner',
      name: 'Evening Elegance',
      icon: 'ChefHat',
      price: 35,
      description: 'Sophisticated dinner service for special occasions',
      includes: [
        'Three-course Italian dinner',
        'Antipasto platter',
        'Choice of two main courses',
        'Dessert selection',
        'Professional service staff',
        'Premium tableware and linens'
      ],
      addons: [
        { id: 'wine', name: 'Wine pairing service', price: 12 },
        { id: 'appetizers', name: 'Passed appetizers', price: 10 },
        { id: 'cake', name: 'Custom celebration cake', price: 15 }
      ],
      minGuests: 25,
      popular: false
    },
    {
      id: 'full-day',
      name: 'All-Day Service',
      icon: 'Clock',
      price: 65,
      description: 'Complete catering for full-day conferences and events',
      includes: [
        'Continental breakfast service',
        'Mid-morning coffee break',
        'Full lunch service',
        'Afternoon refreshments',
        'All beverages throughout day',
        'Dedicated service team'
      ],
      addons: [
        { id: 'dinner-addon', name: 'Add dinner service', price: 20 },
        { id: 'snack-bar', name: 'Continuous snack bar', price: 8 },
        { id: 'barista', name: 'On-site barista', price: 15 }
      ],
      minGuests: 30,
      popular: true
    }
  ];

  const handleAddonToggle = (packageId, addonId) => {
    const key = `${packageId}-${addonId}`;
    setCustomizations(prev =>
      prev?.includes(key)
        ? prev?.filter(item => item !== key)
        : [...prev, key]
    );
  };

  return (
    <section className="py-16 bg-background">
      <div className="container-custom">
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 mb-4">
            <Icon name="Package" size={18} color="var(--color-primary)" />
            <span className="text-sm font-medium text-primary">Catering Packages</span>
          </div>
          <h2 className="font-serif text-4xl font-bold text-foreground mb-4">
            Choose Your Perfect Package
          </h2>
          <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
            Flexible catering solutions designed for every occasion. Customize any package to match your event needs.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mb-12">
          {packages?.map((pkg) => (
            <div
              key={pkg?.id}
              className={`bg-card rounded-2xl shadow-warm overflow-hidden transition-all duration-300 ${
                selectedPackage === pkg?.id ? 'ring-2 ring-primary' : ''
              } ${pkg?.popular ? 'border-2 border-primary' : 'border border-border'}`}
            >
              {pkg?.popular && (
                <div className="bg-primary text-primary-foreground text-center py-2 text-sm font-medium">
                  Most Popular Choice
                </div>
              )}

              <div className="p-8">
                <div className="flex items-start justify-between mb-6">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 rounded-xl bg-primary/10 flex items-center justify-center">
                      <Icon name={pkg?.icon} size={28} color="var(--color-primary)" />
                    </div>
                    <div>
                      <h3 className="font-serif text-2xl font-bold text-foreground">
                        {pkg?.name}
                      </h3>
                      <p className="text-sm text-muted-foreground mt-1">
                        Min. {pkg?.minGuests} guests
                      </p>
                    </div>
                  </div>
                  <div className="text-right">
                    <div className="text-3xl font-bold text-primary">
                      ${pkg?.price}
                    </div>
                    <div className="text-sm text-muted-foreground">per person</div>
                  </div>
                </div>

                <p className="text-muted-foreground mb-6">
                  {pkg?.description}
                </p>

                <div className="mb-6">
                  <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                    <Icon name="Check" size={18} color="var(--color-success)" />
                    What's Included
                  </h4>
                  <ul className="space-y-2">
                    {pkg?.includes?.map((item, index) => (
                      <li key={index} className="flex items-start gap-2 text-sm text-muted-foreground">
                        <Icon name="Dot" size={20} color="var(--color-primary)" className="flex-shrink-0 mt-0.5" />
                        {item}
                      </li>
                    ))}
                  </ul>
                </div>

                {selectedPackage === pkg?.id && (
                  <div className="mb-6 p-4 bg-muted rounded-lg">
                    <h4 className="font-semibold text-foreground mb-3 flex items-center gap-2">
                      <Icon name="Plus" size={18} color="var(--color-accent)" />
                      Customize Your Package
                    </h4>
                    <div className="space-y-2">
                      {pkg?.addons?.map((addon) => (
                        <Checkbox
                          key={addon?.id}
                          label={`${addon?.name} (+$${addon?.price}/person)`}
                          checked={customizations?.includes(`${pkg?.id}-${addon?.id}`)}
                          onChange={() => handleAddonToggle(pkg?.id, addon?.id)}
                        />
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-3">
                  <Button
                    variant={selectedPackage === pkg?.id ? 'default' : 'outline'}
                    fullWidth
                    iconName={selectedPackage === pkg?.id ? 'Check' : 'Package'}
                    iconPosition="left"
                    onClick={() => setSelectedPackage(selectedPackage === pkg?.id ? null : pkg?.id)}
                  >
                    {selectedPackage === pkg?.id ? 'Selected' : 'Select Package'}
                  </Button>
                  <Button
                    variant="ghost"
                    iconName="Settings"
                    onClick={() => {
                      setSelectedPackage(pkg?.id);
                      onCustomizeClick();
                    }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>

        <div className="text-center">
          <div className="inline-flex items-center gap-2 p-4 bg-accent/10 rounded-lg">
            <Icon name="Info" size={20} color="var(--color-accent)" />
            <p className="text-sm text-foreground">
              All packages include setup, service, and cleanup. Custom menus available upon request.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default CateringPackages;